import cx from 'classnames';
import { Field, Formik } from 'formik';
import { useContext, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import styled, { useTheme } from 'styled-components';
import * as Yup from 'yup';

import { getRegion } from '../../api/Location';
import { TariffDirection, getSupportedTariffs } from '../../api/Optimise';
import { OnboardingContext } from '../../context/OnboardingContext';
import { getCurrentUserInfo } from '../../utils/auth';
import { min } from '../../utils/breakpoints';
import convertToTitleCase from '../../utils/convertToTitleCase';
import { getSupplierNames, getTariffsForSupplier, getZeroRateExportTariff } from '../../utils/tariffUtils';
import EnergyIcon from '../EnergyIcon';
import ErrorLabel from '../ErrorLabel';
import LoopSpinner from '../LoopSpinner';
import StyledCheckBox from '../Styled/StyledCheckbox';

import { fetchExportTariffRates, fetchImportTariffRates } from './TariffFormHelpers';
import TariffRatesDisplay, { isRateEmpty } from './TariffRatesDisplay';

const TariffWrapper = styled.div`
  border: 1px solid ${(props) => props.theme.colors.light};
  border-radius: 4px;

  h3 {
    border-bottom: 1px solid ${(props) => props.theme.colors.light};
    padding: 10px 20px;
    margin-bottom: 20px;
    font-size: 16px;
    font-weight: bold;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .form-group {
    padding: 10px 20px;
    margin: 0;

    &:last-child {
      margin-bottom: 10px;
    }
  }
`;

const StyledNote = styled.div`
  font-size: 12px;
  padding: 0px;
`;

const StyledAnchor = styled.a`
  text-decoration: underline !important;
`;

const StyledButton = styled(Button)`
  display: block;
  width: 48%;
  height: 44px;
  margin-bottom: 0;
`;

const StyledCancelButton = styled(StyledButton)`
  background-color: ${(props) => props.theme.colors.light};
  color: ${(props) => props.theme.colors.white};
  border: none;
  text-decoration: none !important;
  margin: 30px 0 0;

  &:hover {
    margin: 30px 0 0;
  }
`;

const StyledFormHeader = styled.h3`
  color: ${(props) => props.theme.colors.themedFormHeaders};
`;

const StyledConfirmation = styled.div`
  margin-left: 32px;
  font-size: 14px;

  ${min.sm} {
    font-size: 12px;
  }
`;

const TariffForm = ({ onSubmit, setInitialValues, cancelPath = null }) => {
  const {
    exportSupplier,
    setExportSupplier,
    importSupplier,
    setImportSupplier,
    importTariffId,
    exportTariffId,
    setExportTariffs: setContextExportTariffs,
  } = useContext(OnboardingContext);
  const [importSuppliers, setImportSuppliers] = useState([]);
  const [importSupplierTariffs, setImportSupplierTariffs] = useState([]);
  const [exportSuppliers, setExportSuppliers] = useState([]);
  const [exportSupplierTariffs, setExportSupplierTariffs] = useState([]);
  const [importTariffs, setImportTariffs] = useState([]);
  const [exportTariffs, setExportTariffs] = useState([]);
  const [regionId, setRegionId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // States used to manage the extra info boxes after picking a tariff
  const [importTariffRates, setImportTariffRates] = useState(null);
  const [loadingImportTariffRates, setLoadingImportTariffRates] = useState(false);
  const [exportTariffRates, setExportTariffRates] = useState(null);
  const [loadingExportTariffRates, setLoadingExportTariffRates] = useState(false);

  const theme = useTheme();

  const validationSchema = Yup.object().shape({
    importSupplier: Yup.string()
      .nullable()
      .trim()
      .test('validateImport', 'Please select your import energy supplier', function (value) {
        const { importTariffId } = this.parent;
        // Only require if import section is being edited
        return !importTariffId || (importTariffId && value);
      }),
    importTariffId: Yup.string()
      .nullable()
      .trim()
      .test('validateImportTariff', 'Please select your import tariff', function (value) {
        const { importSupplier } = this.parent;
        // Only require if import section is being edited
        return !importSupplier || (importSupplier && value);
      }),
    exportSupplier: Yup.string()
      .nullable()
      .trim()
      .test('validateExport', 'Please select your export energy supplier', function (value) {
        const { exportTariffId } = this.parent;
        const { confirm } = this.parent;
        // Skip validation if "no export tariff" is checked
        if (confirm) return true;
        // Only require if export section is being edited
        return !exportTariffId || (exportTariffId && value);
      }),
    exportTariffId: Yup.string()
      .nullable()
      .trim()
      .test('validateExportTariff', 'Please select your export tariff', function (value) {
        const { exportSupplier } = this.parent;
        const { confirm } = this.parent;
        // Skip validation if "no export tariff" is checked
        if (confirm) return true;
        // Only require if export section is being edited
        return !exportSupplier || (exportSupplier && value);
      }),
  });

  const handleConfirmCheckboxChange = (
    field,
    form,

    selectExportSupplier
  ) => {
    const newValue = !field.value;

    form.setFieldValue('confirm', newValue);

    if (newValue) {
      // Clear export values
      form.setFieldValue('exportSupplier', '');
      form.setFieldValue('exportTariffId', '');
      selectExportSupplier('');

      form.setFieldError('exportSupplier', undefined);
      form.setFieldError('exportTariffId', undefined);
      form.setFieldTouched('exportSupplier', false, false);
      form.setFieldTouched('exportTariffId', false, false);
    }

    // Re-run Yup validation after setting everything
    setTimeout(() => {
      form.validateForm();
    }, 0);
  };

  const selectImportSupplier = (supplier, setFieldValue) => {
    setImportSupplier(supplier);
    // Clear tariff rates and reset tariff selection when supplier changes
    setImportTariffRates(null);
    setLoadingImportTariffRates(false);
    // Reset the form field value
    if (setFieldValue) {
      setFieldValue('importTariffId', null);
    }
    // Set the new supplier's tariffs or clear if no supplier selected
    if (supplier) {
      setImportSupplierTariffs(getTariffsForSupplier(supplier, importTariffs));
    } else {
      setImportSupplierTariffs([]);
    }
  };

  const selectExportSupplier = (supplier, setFieldValue) => {
    setExportSupplier(supplier);
    // Clear tariff rates and reset tariff selection when supplier changes
    setExportTariffRates(null);
    setLoadingExportTariffRates(false);
    // Reset the form field value
    if (setFieldValue) {
      setFieldValue('exportTariffId', null);
    }
    // Set the new supplier's tariffs or clear if no supplier selected
    if (supplier) {
      setExportSupplierTariffs(getTariffsForSupplier(supplier, exportTariffs));
    } else {
      setExportSupplierTariffs([]);
    }
  };

  useEffect(() => {
    getCurrentUserInfo().then((user) => {
      const postcode = user.attributes['custom:postcode'];
      if (!!postcode) {
        getRegion(postcode)
          .then((region) => setRegionId(region.distribution_id))
          .catch((e) => console.error(e));
      }
    });
  }, []);

  useEffect(() => {
    if (!!regionId) {
      getSupportedTariffs(TariffDirection.IMPORT, regionId).then((res) => setImportTariffs(res?.supported_tariffs));
      getSupportedTariffs(TariffDirection.EXPORT, regionId).then((res) => {
        const tariffs = res?.supported_tariffs;
        setExportTariffs(tariffs);
        if (setContextExportTariffs && tariffs) {
          setContextExportTariffs(tariffs);
        }
      });
    }
  }, [regionId, setContextExportTariffs]);

  useEffect(() => {
    const importSupplierNames = getSupplierNames(importTariffs);
    setImportSuppliers(importSupplierNames);
  }, [importTariffs]);

  useEffect(() => {
    const exportSupplierNames = getSupplierNames(exportTariffs);
    setExportSuppliers(exportSupplierNames);
  }, [exportTariffs]);

  useEffect(() => {
    if (!!importSupplier) {
      selectImportSupplier(importSupplier, null);
    }
    if (!!exportSupplier) {
      selectExportSupplier(exportSupplier, null);
    }
  }, [importTariffs, exportTariffs]);

  useEffect(() => {
    if (importTariffId) {
      fetchImportTariffRates(importTariffId, setImportTariffRates, setLoadingImportTariffRates);
    } else {
      setImportTariffRates(null);
    }
  }, [importTariffId]);

  useEffect(() => {
    if (exportTariffId) {
      fetchExportTariffRates(exportTariffId, setExportTariffRates, setLoadingExportTariffRates);
    } else {
      setExportTariffRates(null);
    }
  }, [exportTariffId]);

  const initialValues = setInitialValues
    ? {
        importSupplier: importSupplier ?? '',
        importTariffId: '',
        exportSupplier: exportSupplier ?? '',
        exportTariffId: '',
        confirm: false,
      }
    : {
        importSupplier: '',
        importTariffId: '',
        exportSupplier: '',
        exportTariffId: '',
        confirm: false,
      };

  const onSubmitWrapper = async (values) => {
    setSubmitting(true);

    const updatedValues = {
      // Only include import values if they've changed
      ...(values.importSupplier &&
        values.importTariffId && {
          importSupplier: values.importSupplier,
          importTariffId: values.importTariffId,
        }),
      // Only include export values if they've changed and confirm is false
      ...(!values.confirm &&
        values.exportSupplier &&
        values.exportTariffId && {
          exportSupplier: values.exportSupplier,
          exportTariffId: values.exportTariffId,
        }),
      confirm: values.confirm,
      exportTariffs: exportTariffs,
    };

    await onSubmit(updatedValues);
    setSubmitting(false);
  };

  const sortBySupplierName = (a, b) => a.localeCompare(b);
  const sortByTariffName = (a, b) => a.tariff_name.localeCompare(b.tariff_name);

  return importSuppliers?.length && exportSuppliers?.length ? (
    <div className="text-left">
      <Formik
        validationSchema={validationSchema}
        onSubmit={onSubmitWrapper}
        initialValues={initialValues}
        validateOnChange
      >
        {({ handleSubmit, isValid, values, touched, dirty, submitCount, setFieldValue }) => {
          const hasInteracted = dirty || submitCount > 0 || Object.values(touched).some(Boolean);

          const isButtonDisabled =
            submitting ||
            !hasInteracted ||
            !isValid ||
            // Check if at least one section (import or export) is filled
            !(
              (values.importSupplier && values.importTariffId) ||
              (values.exportSupplier && values.exportTariffId) ||
              values.confirm
            );

          return (
            <Form onSubmit={handleSubmit}>
              <TariffWrapper className="mb-4">
                <StyledFormHeader>
                  <span>Import details</span>
                  <EnergyIcon direction={TariffDirection.IMPORT} />
                </StyledFormHeader>
                <Field name="importSupplier">
                  {({ field, meta, form }) => (
                    <Form.Group controlId="importSupplier" className="pb-4">
                      <Form.Label>Who's your import energy provider?</Form.Label>
                      <Form.Control
                        as="select"
                        size="md"
                        value={field.value}
                        onChange={(e) => {
                          selectImportSupplier(e.target.value, setFieldValue);
                          field.onChange(e);
                          form.setFieldValue('importTariffId', '');
                        }}
                        onBlur={field.onBlur}
                        isValid={!meta.error && meta.touched}
                        placeholder="Who's your import energy provider?"
                        className={cx({ error: !!meta.error })}
                      >
                        <optgroup>
                          <option value="" key="supplier-select">
                            Who's your import energy provider?
                          </option>
                          {importSuppliers
                            ?.slice()
                            .sort(sortBySupplierName)
                            .map((supplier, index) => (
                              <option key={`import-supplier-${index}`} value={supplier}>
                                {convertToTitleCase(supplier)}
                              </option>
                            ))}
                        </optgroup>
                      </Form.Control>
                      {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                    </Form.Group>
                  )}
                </Field>
                <Field name="importTariffId">
                  {({ field, meta }) => (
                    <Form.Group controlId="importTariffId">
                      <Form.Label>What's your import tariff?</Form.Label>
                      <Form.Control
                        as="select"
                        size="md"
                        value={field.value ?? ''}
                        onChange={(e) => {
                          field.onChange(e);
                          if (e.target.value) {
                            fetchImportTariffRates(e.target.value, setImportTariffRates, setLoadingImportTariffRates);
                          } else {
                            setImportTariffRates(null);
                          }
                        }}
                        onBlur={field.onBlur}
                        isValid={!meta.error && meta.touched}
                        placeholder="What's import your tariff?"
                        disabled={!importSupplierTariffs.length}
                        className={cx({ error: !!meta.error })}
                      >
                        <optgroup>
                          <option value="" key="import-tariff-select">
                            What's your import tariff?
                          </option>
                          {importSupplierTariffs
                            ?.slice()
                            .sort(sortByTariffName)
                            .map(({ tariff_id, tariff_name }, index) => (
                              <option key={`import-tariff-${index}`} value={tariff_id}>
                                {tariff_name}
                              </option>
                            ))}
                        </optgroup>
                      </Form.Control>
                      {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                    </Form.Group>
                  )}
                </Field>
                <Form.Group className="not-found-text">
                  <div>
                    Can't find your import tariff?
                    <StyledAnchor
                      href="https://share.hsforms.com/1qFeVIeHORx-7a6BChR_1qA2uro2"
                      target="_blank"
                      rel="noreferrer"
                      className="p-0 not-found-text ml-1"
                    >
                      Let us know here
                    </StyledAnchor>
                  </div>
                </Form.Group>

                {/* This section is a box that contains the tariff rates for the selected IMPORT tariff */}
                <Form.Group>
                  <TariffRatesDisplay
                    loading={loadingImportTariffRates}
                    tariffRates={importTariffRates}
                    label="Import rates for this tariff"
                  />
                </Form.Group>

                {/* This is a link to the support page */}
                {(loadingImportTariffRates || importTariffRates) && (
                  <Form.Group className="not-found-text mt-0">
                    <div>
                      Are these rates incorrect?
                      <StyledAnchor
                        href="https://kb.loop.homes/kb-tickets/new"
                        target="_blank"
                        rel="noreferrer"
                        className="p-0 not-found-text ml-1"
                      >
                        Let us know here
                      </StyledAnchor>
                    </div>
                  </Form.Group>
                )}
              </TariffWrapper>
              <TariffWrapper>
                <StyledFormHeader>
                  Export details
                  <EnergyIcon direction={TariffDirection.EXPORT} />
                </StyledFormHeader>
                <Field name="exportSupplier">
                  {({ field, meta, form }) => (
                    <Form.Group controlId="exportSupplier" className="pb-4">
                      <Form.Label>Who's your export energy provider?</Form.Label>
                      <Form.Control
                        as="select"
                        size="md"
                        value={field.value}
                        disabled={form.values.confirm}
                        onChange={(e) => {
                          selectExportSupplier(e.target.value, setFieldValue);
                          field.onChange(e);
                          form.setFieldValue('exportTariffId', '');
                        }}
                        onBlur={field.onBlur}
                        isValid={!meta.error && meta.touched}
                        placeholder="Who's your export energy provider?"
                        className={cx({ error: !!meta.error })}
                      >
                        <optgroup>
                          <option value="" key="export-supplier-select">
                            Who's your export energy provider?
                          </option>
                          {exportSuppliers
                            ?.slice()
                            .sort(sortBySupplierName)
                            .map((supplier, index) => (
                              <option key={`export-supplier-${index}`} value={supplier}>
                                {convertToTitleCase(supplier)}
                              </option>
                            ))}
                        </optgroup>
                      </Form.Control>
                      {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                    </Form.Group>
                  )}
                </Field>
                <Field name="exportTariffId">
                  {({ field, meta }) => (
                    <Form.Group controlId="exportTariffId">
                      <Form.Label>What's your export tariff?</Form.Label>
                      <Form.Control
                        as="select"
                        size="md"
                        value={field.value}
                        onChange={(e) => {
                          field.onChange(e);
                          if (e.target.value) {
                            fetchExportTariffRates(e.target.value, setExportTariffRates, setLoadingExportTariffRates);
                          } else {
                            setExportTariffRates(null);
                          }
                        }}
                        onBlur={field.onBlur}
                        isValid={!meta.error && meta.touched}
                        placeholder="What's your export tariff?"
                        disabled={!exportSupplierTariffs.length}
                        className={cx({ error: !!meta.error })}
                      >
                        <optgroup>
                          <option value="" key="export-tariff-select">
                            What's your export tariff?
                          </option>
                          {exportSupplierTariffs
                            ?.slice()
                            .sort(sortByTariffName)
                            .map(({ tariff_id, tariff_name }, index) => (
                              <option key={`export-tariff-${index}`} value={tariff_id}>
                                {tariff_name}
                              </option>
                            ))}
                        </optgroup>
                      </Form.Control>
                      {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                    </Form.Group>
                  )}
                </Field>
                <Form.Group className="not-found-text">
                  <div>
                    Can't find your export tariff?
                    <StyledAnchor
                      href="https://share.hsforms.com/1qFeVIeHORx-7a6BChR_1qA2uro2"
                      target="_blank"
                      rel="noreferrer"
                      className="p-0 not-found-text ml-1"
                    >
                      Let us know here
                    </StyledAnchor>
                  </div>
                </Form.Group>
                {/* This section is a box that contains the tariff rates for the selected EXPORT tariff */}
                <Form.Group>
                  <TariffRatesDisplay
                    loading={loadingExportTariffRates}
                    tariffRates={exportTariffRates}
                    label="Export rates for this tariff"
                  />
                </Form.Group>
                {/* This is a link to the support page */}
                {(loadingExportTariffRates || !isRateEmpty(exportTariffRates)) && (
                  <Form.Group className="not-found-text mt-0 mb-2">
                    <div>
                      Are these rates incorrect?
                      <StyledAnchor
                        href="https://kb.loop.homes/kb-tickets/new"
                        target="_blank"
                        rel="noreferrer"
                        className="p-0 not-found-text ml-1"
                      >
                        Let us know here
                      </StyledAnchor>
                    </div>
                  </Form.Group>
                )}
                <Field name="confirm">
                  {({ field, meta, form }) => (
                    <StyledCheckBox
                      style={{ padding: '0px 20px', lineHeight: '22px' }}
                      className={`text-left ${cx({
                        selected: field.value,
                        error: !!meta.error,
                      })}`}
                    >
                      <Form.Check
                        type="checkbox"
                        name="confirm"
                        id="confirm-check"
                        onChange={() => handleConfirmCheckboxChange(field, form, selectExportSupplier)}
                        onBlur={field.onBlur}
                        className={cx({ error: !!meta.error })}
                        label={<StyledConfirmation>I don't have an export tariff yet.</StyledConfirmation>}
                      />
                      {meta.error && meta.touched && <ErrorLabel pad={false} showIcon={false} label={meta.error} />}
                    </StyledCheckBox>
                  )}
                </Field>
                <Form.Group>
                  <StyledNote>
                    <span
                      style={{
                        color: theme.colors.primaryHover,
                        fontWeight: 700,
                      }}
                    >
                      NOTE:
                    </span>{' '}
                    For new installations, it can take 8 weeks to get an export tariff from your energy supplier.{' '}
                    <b>Once you know your export tariff you can add it in Settings.</b>
                  </StyledNote>
                </Form.Group>
              </TariffWrapper>
              <Form.Group>
                {!submitting ? (
                  <>
                    {cancelPath ? (
                      <div className="d-flex justify-content-between">
                        <StyledCancelButton variant="light" href={cancelPath}>
                          Cancel
                        </StyledCancelButton>
                        <StyledButton variant="primary" disabled={isButtonDisabled} type="submit">
                          Confirm
                        </StyledButton>
                      </div>
                    ) : (
                      <Button variant="primary" disabled={isButtonDisabled} block className="mb-0" type="submit">
                        Continue
                      </Button>
                    )}
                  </>
                ) : (
                  <div className="mt-4">
                    <LoopSpinner />
                  </div>
                )}
              </Form.Group>
            </Form>
          );
        }}
      </Formik>
    </div>
  ) : (
    <div className="mt-4">
      <LoopSpinner />
    </div>
  );
};

export default TariffForm;
